(() => {
  'use strict';

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  class MobileNavigation {
    constructor() {
      this.button = qs('[data-mobile-menu-button]');
      this.menu = qs('[data-mobile-menu]');
      if (!this.button || !this.menu) return;
      this.button.addEventListener('click', () => this.toggle());
      qsa('a', this.menu).forEach((link) => link.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }
    toggle() {
      const open = this.button.getAttribute('aria-expanded') === 'true';
      open ? this.close() : this.open();
    }
    open() {
      this.button.setAttribute('aria-expanded', 'true');
      this.menu.classList.add('is-open');
      this.menu.hidden = false;
    }
    close() {
      this.button.setAttribute('aria-expanded', 'false');
      this.menu.classList.remove('is-open');
      this.menu.hidden = true;
    }
  }

  class QuantityInput {
    constructor(element) {
      this.element = element;
      this.input = qs('input[type="number"]', element);
      if (!this.input) return;
      qsa('button', element).forEach((button) => {
        button.addEventListener('click', () => this.change(button.dataset.quantityAction));
      });
    }
    change(action) {
      const step = Number(this.input.step || 1);
      const min = Number(this.input.min || 1);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const current = Number(this.input.value || min);
      const next = action === 'increase' ? current + step : current - step;
      this.input.value = String(Math.min(max, Math.max(min, next)));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  class Carousel {
    constructor(element) {
      this.element = element;
      this.track = qs('[data-carousel-track]', element);
      this.slides = qsa('[data-carousel-slide]', element);
      this.prev = qs('[data-carousel-prev]', element);
      this.next = qs('[data-carousel-next]', element);
      this.status = qs('[data-carousel-status]', element);
      this.index = 0;
      if (!this.track || this.slides.length < 1) return;
      this.prev?.addEventListener('click', () => this.move(-1));
      this.next?.addEventListener('click', () => this.move(1));
      this.element.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.move(-1);
        if (event.key === 'ArrowRight') this.move(1);
      });
      this.touchStartX = null;
      this.element.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches[0]?.clientX ?? null;
      }, { passive: true });
      this.element.addEventListener('touchend', (event) => {
        if (this.touchStartX === null) return;
        const endX = event.changedTouches[0]?.clientX ?? this.touchStartX;
        const delta = endX - this.touchStartX;
        if (Math.abs(delta) > 42) this.move(delta < 0 ? 1 : -1);
        this.touchStartX = null;
      }, { passive: true });
      this.update();
    }
    visibleSlides() {
      if (this.element.classList.contains('zc-carousel--journal')) return 1;
      return window.matchMedia('(max-width: 680px)').matches ? 1 : 2;
    }
    slideWidth() {
      if (this.element.classList.contains('zc-carousel--journal')) return 100;
      return window.matchMedia('(max-width: 680px)').matches ? 88 : 50;
    }
    move(delta) {
      const maximum = Math.max(0, this.slides.length - this.visibleSlides());
      this.index = Math.max(0, Math.min(maximum, this.index + delta));
      this.update();
    }
    update() {
      const width = this.slideWidth();
      this.track.style.transform = `translateX(-${this.index * width}%)`;
      if (this.prev) this.prev.disabled = this.index === 0;
      const maximum = Math.max(0, this.slides.length - this.visibleSlides());
      if (this.next) this.next.disabled = this.index >= maximum;
      if (this.status) this.status.textContent = `${this.index + 1} / ${Math.max(1, maximum + 1)}`;
    }
  }

  class BandFinder {
    constructor(element) {
      this.element = element;
      this.choices = qsa('[data-finder-choice]', element);
      this.result = qs('[data-finder-result]', element);
      if (!this.choices.length || !this.result) return;
      this.choices.forEach((choice) => choice.addEventListener('click', () => this.select(choice)));
    }
    select(choice) {
      this.choices.forEach((item) => item.setAttribute('aria-pressed', String(item === choice)));
      const title = choice.dataset.resultTitle || '';
      const text = choice.dataset.resultText || '';
      const phases = choice.dataset.resultPhases || '';
      const href = choice.dataset.resultHref || '#';
      const image = choice.dataset.resultImage || '';
      const color = choice.dataset.resultColor || '#ae6f50';
      const titleNode = qs('[data-finder-result-title]', this.result);
      const textNode = qs('[data-finder-result-text]', this.result);
      const linkNode = qs('[data-finder-result-link]', this.result);
      const imageNode = qs('[data-finder-result-image]', this.result);
      const phasesNode = qs('[data-finder-result-phases]', this.result);
      const phasesList = qs('.zc-finder-cover__result-phases-list', this.result);
      if (titleNode) titleNode.textContent = title;
      if (textNode) textNode.textContent = text;
      if (phasesNode && phasesList) {
        const items = phases.split('|').map((item) => item.trim()).filter(Boolean);
        phasesList.innerHTML = '';
        items.forEach((item) => {
          const li = document.createElement('li');
          li.textContent = item;
          phasesList.appendChild(li);
        });
        phasesNode.hidden = items.length === 0;
      }
      if (linkNode) linkNode.href = href;
      if (imageNode && image) {
        imageNode.src = image;
        imageNode.alt = `Cover des empfohlenen Bandes ${title}`;
      }
      this.result.style.setProperty('--band-color', color);
      this.element.classList.add('has-result');
      this.result.classList.add('is-visible');
      this.result.hidden = false;
      this.result.setAttribute('tabindex', '-1');
      this.result.focus({ preventScroll: true });
      this.result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    }
  }

  class CartDrawer {
    constructor() {
      this.drawer = qs('[data-cart-drawer]');
      this.openButtons = qsa('[data-cart-drawer-open]');
      if (!this.drawer) return;
      this.panel = qs('[data-cart-drawer-panel]', this.drawer);
      this.closeButtons = qsa('[data-cart-drawer-close]', this.drawer);
      this.openButtons.forEach((button) => button.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      }));
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      this.bindActions();
    }
    open() {
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      setTimeout(() => qs('[data-cart-drawer-close]', this.drawer)?.focus(), 20);
    }
    close() {
      if (this.drawer.getAttribute('aria-hidden') === 'true') return;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
    }
    bindActions() {
      qsa('[data-cart-line-change]', this.drawer).forEach((control) => {
        control.addEventListener('change', () => this.change(control.dataset.line, Number(control.value)));
      });
      qsa('[data-cart-line-remove]', this.drawer).forEach((control) => {
        control.addEventListener('click', () => this.change(control.dataset.line, 0));
      });
    }
    async change(line, quantity) {
      try {
        await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity })
        });
        await this.refresh();
      } catch (error) {
        console.error('Cart update failed', error);
      }
    }
    async refresh() {
      try {
        const root = window.routes?.root_url || '/';
        const response = await fetch(`${root}?sections=cart-drawer`);
        const sections = await response.json();
        const html = sections['cart-drawer'];
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = qs('[data-cart-drawer]', parsed);
        if (!next) return;
        this.drawer.replaceWith(next);
        this.drawer = next;
        this.panel = qs('[data-cart-drawer-panel]', this.drawer);
        this.closeButtons = qsa('[data-cart-drawer-close]', this.drawer);
        this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
        this.bindActions();
        await this.updateCount();
        this.open();
      } catch (error) {
        console.error('Cart drawer refresh failed', error);
      }
    }
    async updateCount() {
      try {
        const response = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js');
        const cart = await response.json();
        qsa('[data-cart-count]').forEach((node) => {
          node.textContent = String(cart.item_count);
          node.hidden = cart.item_count === 0;
        });
      } catch (_) {}
    }
  }

  class ProductForm {
    constructor(form, cartDrawer) {
      this.form = form;
      this.cartDrawer = cartDrawer;
      this.button = qs('[type="submit"]', form);
      this.error = qs('[data-product-form-error]', form);
      form.addEventListener('submit', (event) => this.submit(event));
    }
    async submit(event) {
      if (!window.fetch || !this.cartDrawer) return;
      event.preventDefault();
      this.setLoading(true);
      if (this.error) { this.error.hidden = true; this.error.textContent = ''; }
      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Produkt konnte nicht hinzugefügt werden.');
        await this.cartDrawer.refresh();
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        }
      } finally {
        this.setLoading(false);
      }
    }
    setLoading(loading) {
      if (!this.button) return;
      this.button.disabled = loading;
      this.button.setAttribute('aria-busy', String(loading));
      const label = this.button.dataset.defaultLabel || this.button.textContent;
      this.button.dataset.defaultLabel = label;
      this.button.textContent = loading ? 'Wird hinzugefügt …' : label;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    new MobileNavigation();
    qsa('[data-quantity]').forEach((element) => new QuantityInput(element));
    qsa('[data-carousel]').forEach((element) => new Carousel(element));
    qsa('[data-band-finder]').forEach((element) => new BandFinder(element));
    const cartDrawer = new CartDrawer();
    qsa('form.zc-product-form').forEach((form) => new ProductForm(form, cartDrawer.drawer ? cartDrawer : null));
  });
})();
